import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextEditor, stripHtml } from '@/components/RichTextEditor';
import { Plus, Trash2, Save, FlaskConical, User, Stethoscope, FileText, Pencil, Upload, ClipboardList, MessageSquare, Clock, Wifi, WifiOff, CheckCircle2, AlertTriangle, Coffee, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ConfiguracoesUsuarioPage({ user, onLogout, showConfig }) {
  const [activeSection, setActiveSection] = useState('exames');

  // Exames state
  const [exames, setExames] = useState([]);
  const [totalExamesClinico, setTotalExamesClinico] = useState(0);
  const [totalExamesImagem, setTotalExamesImagem] = useState(0);
  const [novoExame, setNovoExame] = useState('');
  const [loadingExames, setLoadingExames] = useState(true);
  const [salvandoExame, setSalvandoExame] = useState(false);
  const [showImportLote, setShowImportLote] = useState(false);
  const [textoLote, setTextoLote] = useState('');
  const [importandoLote, setImportandoLote] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  // Categoria ativa (clinico | imagem) e edição inline de nome
  const [categoriaExameAtiva, setCategoriaExameAtiva] = useState('clinico');
  const [editandoExameId, setEditandoExameId] = useState(null);
  const [nomeEditando, setNomeEditando] = useState('');
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  // Dados do médico state
  const [configMedico, setConfigMedico] = useState({
    nome_medico: '',
    crm: '',
    especialidade: '',
    telefone: '',
    instagram: '',
    endereco: '',
    exibir_qr: true
  });
  const [salvandoConfig, setSalvandoConfig] = useState(false);

  // Modelos de receituário state
  const [modelos, setModelos] = useState([]);
  const [loadingModelos, setLoadingModelos] = useState(true);
  const [salvandoModelo, setSalvandoModelo] = useState(false);
  const [editandoModelo, setEditandoModelo] = useState(null);
  const [formModelo, setFormModelo] = useState({ nome: '', tipo: 'receituario', conteudo: '' });

  // WhatsApp / Evolution API state (master only)
  const isMasterAccess = user?.is_master_access === true;
  const [evolutionConfig, setEvolutionConfig] = useState({
    evolution_api_url: '', evolution_api_key: '', evolution_instance_name: ''
  });
  const [horariosFunc, setHorariosFunc] = useState({});
  const [duracaoPadraoMin, setDuracaoPadraoMin] = useState('');
  const [almocoInicio, setAlmocoInicio] = useState('');
  const [almocoFim, setAlmocoFim] = useState('');
  const [templateCancel, setTemplateCancel] = useState('');
  const [salvandoEvolution, setSalvandoEvolution] = useState(false);
  const [salvandoHorarios, setSalvandoHorarios] = useState(false);
  const [salvandoTemplate, setSalvandoTemplate] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Financeiro toggle (Master only)
  const [financeiroEnabled, setFinanceiroEnabled] = useState(false);
  const [financeiroDataInicio, setFinanceiroDataInicio] = useState('');
  const [salvandoFinanceiro, setSalvandoFinanceiro] = useState(false);

  useEffect(() => {
    loadExames();
    loadConfigMedico();
    loadModelos();
    if (isMasterAccess) {
      loadEvolutionConfig();
      loadHorariosFunc();
      loadTemplateCancel();
      loadFinanceiroConfig();
    }
  }, []);

  // Recarrega exames ao trocar a aba de categoria (Clínicos/Imagem)
  useEffect(() => {
    setLoadingExames(true);
    setEditandoExameId(null);
    setNomeEditando('');
    setResultadoImport(null);
    loadExames();
  }, [categoriaExameAtiva]);

  const loadFinanceiroConfig = async () => {
    try {
      const res = await axios.get(`${API}/config/financeiro`);
      setFinanceiroEnabled(!!res.data.enabled);
      setFinanceiroDataInicio(res.data.data_inicio || '');
    } catch (e) { console.error('Erro ao carregar config financeira:', e); }
  };

  const handleSalvarFinanceiro = async () => {
    setSalvandoFinanceiro(true);
    try {
      await axios.put(`${API}/config/financeiro`, {
        enabled: financeiroEnabled,
        data_inicio: financeiroEnabled ? (financeiroDataInicio || null) : null
      });
      toast.success('Configuração do recurso financeiro salva!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar configuração financeira');
    } finally { setSalvandoFinanceiro(false); }
  };

  const loadEvolutionConfig = async () => {
    try {
      const res = await axios.get(`${API}/config-whatsapp/evolution`);
      setEvolutionConfig({
        evolution_api_url: res.data.evolution_api_url || '',
        evolution_api_key: res.data.evolution_api_key || '',
        evolution_instance_name: res.data.evolution_instance_name || ''
      });
    } catch (e) { console.error('Erro ao carregar config evolution:', e); }
  };

  const loadHorariosFunc = async () => {
    try {
      const res = await axios.get(`${API}/config-whatsapp/horarios`);
      setHorariosFunc(res.data.horarios || {});
      setDuracaoPadraoMin(res.data.duracao_padrao_min != null ? String(res.data.duracao_padrao_min) : '');
      setAlmocoInicio(res.data.almoco_inicio || '');
      setAlmocoFim(res.data.almoco_fim || '');
    } catch (e) { console.error('Erro ao carregar horários:', e); }
  };

  const loadTemplateCancel = async () => {
    try {
      const res = await axios.get(`${API}/config-whatsapp/template`);
      setTemplateCancel(res.data.template || '');
    } catch (e) { console.error('Erro ao carregar template:', e); }
  };

  const handleSalvarEvolution = async () => {
    setSalvandoEvolution(true);
    try {
      await axios.put(`${API}/config-whatsapp/evolution`, evolutionConfig);
      toast.success('Configuração da Evolution API salva!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar configuração');
    } finally { setSalvandoEvolution(false); }
  };

  const handleSalvarHorarios = async () => {
    setSalvandoHorarios(true);
    try {
      const duracaoNum = duracaoPadraoMin === '' ? null : parseInt(duracaoPadraoMin, 10);
      if (duracaoNum !== null && (isNaN(duracaoNum) || duracaoNum < 5 || duracaoNum > 480)) {
        toast.error('Duração padrão deve estar entre 5 e 480 minutos.');
        setSalvandoHorarios(false);
        return;
      }
      // Validate lunch break locally
      const aIni = (almocoInicio || '').trim();
      const aFim = (almocoFim || '').trim();
      if ((aIni && !aFim) || (aFim && !aIni)) {
        toast.error('Para configurar o almoço, informe início E fim.');
        setSalvandoHorarios(false);
        return;
      }
      if (aIni && aFim) {
        const [ah, am] = aIni.split(':').map(Number);
        const [bh, bm] = aFim.split(':').map(Number);
        if ((ah * 60 + am) >= (bh * 60 + bm)) {
          toast.error('O horário final do almoço deve ser maior que o inicial.');
          setSalvandoHorarios(false);
          return;
        }
      }
      await axios.put(`${API}/config-whatsapp/horarios`, {
        horarios: horariosFunc,
        duracao_padrao_min: duracaoNum,
        almoco_inicio: aIni || null,
        almoco_fim: aFim || null,
      });
      toast.success('Horários de funcionamento salvos!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar horários');
    } finally { setSalvandoHorarios(false); }
  };

  const handleSalvarTemplate = async () => {
    setSalvandoTemplate(true);
    try {
      await axios.put(`${API}/config-whatsapp/template`, { template: templateCancel });
      toast.success('Template de cancelamento salvo!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar template');
    } finally { setSalvandoTemplate(false); }
  };

  const handleTestarConexao = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const res = await axios.post(`${API}/config-whatsapp/test`);
      setConnectionStatus(res.data);
    } catch (e) {
      setConnectionStatus({ success: false, message: e.response?.data?.detail || 'Erro ao testar conexão' });
    } finally { setTestingConnection(false); }
  };

  const DIAS_SEMANA = [
    { key: '1', label: 'Segunda-feira' },
    { key: '2', label: 'Terça-feira' },
    { key: '3', label: 'Quarta-feira' },
    { key: '4', label: 'Quinta-feira' },
    { key: '5', label: 'Sexta-feira' },
    { key: '6', label: 'Sábado' },
    { key: '0', label: 'Domingo' },
  ];

  const toggleDia = (diaKey) => {
    setHorariosFunc(prev => {
      const next = { ...prev };
      if (next[diaKey]) {
        delete next[diaKey];
      } else {
        next[diaKey] = { inicio: '08:00', fim: '18:00' };
      }
      return next;
    });
  };

  const updateHorarioDia = (diaKey, field, value) => {
    setHorariosFunc(prev => ({
      ...prev,
      [diaKey]: { ...prev[diaKey], [field]: value }
    }));
  };

  const loadExames = async () => {
    try {
      // Buscar em paralelo: exames da aba atual + contagens totais das duas categorias
      const [respAtiva, respClinico, respImagem] = await Promise.all([
        axios.get(`${API}/exames`, { params: { categoria: categoriaExameAtiva } }),
        axios.get(`${API}/exames`, { params: { categoria: 'clinico' } }),
        axios.get(`${API}/exames`, { params: { categoria: 'imagem' } }),
      ]);
      setExames(respAtiva.data);
      setTotalExamesClinico(respClinico.data.length);
      setTotalExamesImagem(respImagem.data.length);
    } catch (error) {
      toast.error('Erro ao carregar exames');
    } finally {
      setLoadingExames(false);
    }
  };

  const loadModelos = async () => {
    try {
      const response = await axios.get(`${API}/modelos-receituario`);
      setModelos(response.data);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    } finally {
      setLoadingModelos(false);
    }
  };

  const handleSalvarModelo = async () => {
    if (!formModelo.nome.trim()) {
      toast.error('Digite um nome para o modelo');
      return;
    }
    if (!stripHtml(formModelo.conteudo)) {
      toast.error('Digite o conteúdo do modelo');
      return;
    }
    setSalvandoModelo(true);
    try {
      if (editandoModelo) {
        await axios.put(`${API}/modelos-receituario/${editandoModelo.id}`, formModelo);
        toast.success('Modelo atualizado com sucesso');
      } else {
        await axios.post(`${API}/modelos-receituario`, formModelo);
        toast.success('Modelo cadastrado com sucesso');
      }
      setFormModelo({ nome: '', tipo: 'receituario', conteudo: '' });
      setEditandoModelo(null);
      loadModelos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar modelo');
    } finally {
      setSalvandoModelo(false);
    }
  };

  const handleEditarModelo = (modelo) => {
    setEditandoModelo(modelo);
    setFormModelo({ nome: modelo.nome, tipo: modelo.tipo, conteudo: modelo.conteudo });
  };

  const handleRemoverModelo = async (modeloId) => {
    if (!window.confirm('Deseja realmente remover este modelo?')) return;
    try {
      await axios.delete(`${API}/modelos-receituario/${modeloId}`);
      toast.success('Modelo removido');
      loadModelos();
    } catch (error) {
      toast.error('Erro ao remover modelo');
    }
  };

  const handleCancelarEdicao = () => {
    setEditandoModelo(null);
    setFormModelo({ nome: '', tipo: 'receituario', conteudo: '' });
  };

  const loadConfigMedico = async () => {
    try {
      const response = await axios.get(`${API}/configuracoes-medico`);
      setConfigMedico(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const handleAdicionarExame = async () => {
    const nome = novoExame.trim().replace(/\s+/g, ' ');
    if (!nome) {
      toast.error('Digite o nome do exame');
      return;
    }
    setSalvandoExame(true);
    try {
      await axios.post(`${API}/exames`, { nome, categoria: categoriaExameAtiva });
      toast.success('Exame cadastrado com sucesso');
      setNovoExame('');
      loadExames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar exame');
    } finally {
      setSalvandoExame(false);
    }
  };

  const handleIniciarEdicao = (exame) => {
    setEditandoExameId(exame.id);
    setNomeEditando(exame.nome);
  };

  const handleCancelarEdicaoExame = () => {
    setEditandoExameId(null);
    setNomeEditando('');
  };

  const handleSalvarEdicaoExame = async () => {
    const nome = nomeEditando.trim().replace(/\s+/g, ' ');
    if (!nome) {
      toast.error('Nome do exame não pode ficar vazio');
      return;
    }
    setSalvandoEdicao(true);
    try {
      await axios.put(`${API}/exames/${editandoExameId}`, { nome });
      toast.success('Exame atualizado');
      setEditandoExameId(null);
      setNomeEditando('');
      loadExames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar exame');
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const handleRemoverExame = async (exameId) => {
    if (!window.confirm('Deseja realmente remover este exame?')) return;
    try {
      await axios.delete(`${API}/exames/${exameId}`);
      toast.success('Exame removido');
      loadExames();
    } catch (error) {
      toast.error('Erro ao remover exame');
    }
  };

  const handleImportarLote = async () => {
    const linhas = textoLote.split('\n').map(l => l.replace(/^\d+[\.\)\-\s]+/, '').trim()).filter(l => l);
    if (linhas.length === 0) {
      toast.error('Cole pelo menos um nome de exame');
      return;
    }
    setImportandoLote(true);
    setResultadoImport(null);
    try {
      const response = await axios.post(`${API}/exames/importar-lote`, { nomes: linhas, categoria: categoriaExameAtiva });
      setResultadoImport(response.data);
      if (response.data.criados > 0) {
        toast.success(`${response.data.criados} exame(s) importado(s) com sucesso!`);
      }
      if (response.data.duplicados > 0) {
        toast.info(`${response.data.duplicados} exame(s) já existiam e foram ignorados`);
      }
      loadExames();
      setTextoLote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar exames');
    } finally {
      setImportandoLote(false);
    }
  };

  const handleImportarExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Formato inválido. Envie um arquivo .xlsx');
      e.target.value = '';
      return;
    }
    setUploadingExcel(true);
    setResultadoImport(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('categoria', categoriaExameAtiva);
      const response = await axios.post(`${API}/exames/importar-excel`, formData);
      setResultadoImport(response.data);
      if (response.data.criados > 0) {
        toast.success(`${response.data.criados} exame(s) importado(s) do Excel!`);
      }
      if (response.data.duplicados > 0) {
        toast.info(`${response.data.duplicados} exame(s) já existiam e foram ignorados`);
      }
      loadExames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo Excel');
    } finally {
      setUploadingExcel(false);
      e.target.value = '';
    }
  };

  const handleSalvarConfig = async () => {
    setSalvandoConfig(true);
    try {
      const response = await axios.put(`${API}/configuracoes-medico`, configMedico);
      setConfigMedico(response.data);
      toast.success('Dados do médico salvos com sucesso');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSalvandoConfig(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
      <div className="max-w-4xl mx-auto" data-testid="configuracoes-usuario-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
          <p className="text-gray-600 mt-1">Gerencie os dados do médico e a lista de exames</p>
        </div>

        {/* Section Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-4 overflow-x-auto">
            <button
              onClick={() => setActiveSection('exames')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeSection === 'exames'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="section-exames"
            >
              <FlaskConical className="w-4 h-4 inline mr-2" />
              Lista de Exames
            </button>
            <button
              onClick={() => setActiveSection('medico')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeSection === 'medico'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="section-medico"
            >
              <Stethoscope className="w-4 h-4 inline mr-2" />
              Dados do Médico
            </button>
            <button
              onClick={() => setActiveSection('receituario')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                activeSection === 'receituario'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="section-receituario"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Receituário e Orientações
            </button>
            {isMasterAccess && (
              <button
                onClick={() => setActiveSection('whatsapp')}
                className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                  activeSection === 'whatsapp'
                    ? 'border-b-2 border-green-600 text-green-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="section-whatsapp"
              >
                <MessageSquare className="w-4 h-4 inline mr-2" />
                WhatsApp
              </button>
            )}
            {isMasterAccess && (
              <button
                onClick={() => setActiveSection('financeiro')}
                className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                  activeSection === 'financeiro'
                    ? 'border-b-2 border-amber-600 text-amber-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="section-financeiro"
              >
                <DollarSign className="w-4 h-4 inline mr-2" />
                Financeiro
              </button>
            )}
          </div>
        </div>

        {/* Exames Section */}
        {activeSection === 'exames' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Cadastro de Exames</h2>
              <p className="text-sm text-gray-500 mb-4">
                Cadastre os exames em duas categorias. Eles aparecerão nas respectivas abas do Prontuário → Solicitar Exames.
              </p>

              {/* Sub-tabs Clínicos / Imagem */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setCategoriaExameAtiva('clinico')}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold flex items-center justify-center gap-2 transition-colors ${
                    categoriaExameAtiva === 'clinico'
                      ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="sub-tab-exames-clinicos"
                >
                  <span>Exames Clínicos</span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    categoriaExameAtiva === 'clinico' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {totalExamesClinico}
                  </span>
                </button>
                <button
                  onClick={() => setCategoriaExameAtiva('imagem')}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold flex items-center justify-center gap-2 transition-colors ${
                    categoriaExameAtiva === 'imagem'
                      ? 'border-purple-600 bg-purple-50 text-purple-700 shadow-sm'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="sub-tab-exames-imagem"
                >
                  <span>Exames de Imagem</span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    categoriaExameAtiva === 'imagem' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {totalExamesImagem}
                  </span>
                </button>
              </div>

              {/* Add exam form (contextual) */}
              <div className={`rounded-lg p-4 mb-4 border ${
                categoriaExameAtiva === 'imagem' ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'
              }`}>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                  categoriaExameAtiva === 'imagem' ? 'text-purple-900' : 'text-blue-900'
                }`}>
                  + Novo Exame {categoriaExameAtiva === 'imagem' ? 'de Imagem' : 'Clínico'}
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <textarea
                    value={novoExame}
                    onChange={(e) => setNovoExame(e.target.value)}
                    placeholder={categoriaExameAtiva === 'imagem'
                      ? 'Ex: ULTRASSONOGRAFIA OBSTÉTRICA MORFOLÓGICA DE 1º TRIMESTRE...'
                      : 'Ex: Hemograma Completo'}
                    className="flex-1 min-h-[44px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAdicionarExame();
                    }}
                    data-testid="input-novo-exame"
                  />
                  <Button
                    onClick={handleAdicionarExame}
                    disabled={salvandoExame || !novoExame.trim()}
                    className={categoriaExameAtiva === 'imagem'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'}
                    data-testid="btn-adicionar-exame"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
              </div>

              {/* Import in batch toggle */}
              <div className="mb-6">
                <button
                  onClick={() => { setShowImportLote(!showImportLote); setResultadoImport(null); }}
                  className={`text-sm font-medium flex items-center gap-1.5 transition-colors ${
                    categoriaExameAtiva === 'imagem' ? 'text-purple-600 hover:text-purple-800' : 'text-blue-600 hover:text-blue-800'
                  }`}
                  data-testid="btn-toggle-import-lote"
                >
                  <ClipboardList className="w-4 h-4" />
                  {showImportLote
                    ? 'Fechar importação em lote'
                    : `Importar exames ${categoriaExameAtiva === 'imagem' ? 'de imagem' : 'clínicos'} em lote`}
                </button>

                {showImportLote && (
                  <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Option 1: Paste list */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Colar lista de exames
                        </label>
                        <p className="text-xs text-gray-500">Um exame por linha</p>
                        <textarea
                          value={textoLote}
                          onChange={(e) => setTextoLote(e.target.value)}
                          placeholder={categoriaExameAtiva === 'imagem'
                            ? "Ultrassonografia Transvaginal\nMamografia\nDensitometria Óssea"
                            : "Hemograma Completo\nGlicemia em Jejum\nColesterol Total\nTSH\nCreatinina"}
                          className="w-full h-36 p-3 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          data-testid="textarea-import-lote"
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">
                            {textoLote.split('\n').filter(l => l.trim()).length} exame(s) detectado(s)
                          </span>
                          <Button
                            onClick={handleImportarLote}
                            disabled={importandoLote || !textoLote.trim()}
                            size="sm"
                            className={categoriaExameAtiva === 'imagem'
                              ? 'bg-purple-600 hover:bg-purple-700 text-white'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'}
                            data-testid="btn-importar-lista"
                          >
                            {importandoLote ? 'Importando...' : 'Importar Lista'}
                          </Button>
                        </div>
                      </div>

                      {/* Option 2: Excel file */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Importar de arquivo Excel
                        </label>
                        <p className="text-xs text-gray-500">Arquivo .xlsx com nomes de exames</p>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <label className="cursor-pointer">
                            <span className={`text-sm font-medium ${
                              categoriaExameAtiva === 'imagem' ? 'text-purple-600 hover:text-purple-800' : 'text-blue-600 hover:text-blue-800'
                            }`}>
                              {uploadingExcel ? 'Enviando...' : 'Clique para selecionar'}
                            </span>
                            <input
                              type="file"
                              accept=".xlsx,.xls"
                              onChange={handleImportarExcel}
                              className="hidden"
                              disabled={uploadingExcel}
                              data-testid="input-excel-exames"
                            />
                          </label>
                          <p className="text-xs text-gray-400 mt-1">Apenas .xlsx</p>
                        </div>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 border-t border-gray-200 pt-3">
                      💡 Os exames importados serão cadastrados como <strong>
                        {categoriaExameAtiva === 'imagem' ? 'Exames de Imagem' : 'Exames Clínicos'}
                      </strong> (aba atual).
                    </div>

                    {/* Import result */}
                    {resultadoImport && (
                      <div className="p-3 rounded-lg border bg-white space-y-2" data-testid="resultado-import">
                        <p className="text-sm font-medium text-gray-900">Resultado da importação:</p>
                        <div className="flex gap-4 text-sm">
                          <span className="text-green-700">{resultadoImport.criados} criado(s)</span>
                          {resultadoImport.duplicados > 0 && (
                            <span className="text-amber-600">{resultadoImport.duplicados} já existente(s)</span>
                          )}
                          {resultadoImport.vazios > 0 && (
                            <span className="text-gray-400">{resultadoImport.vazios} linha(s) vazia(s)</span>
                          )}
                        </div>
                        {resultadoImport.nomes_duplicados?.length > 0 && (
                          <details className="text-xs text-gray-500">
                            <summary className="cursor-pointer hover:text-gray-700">Ver duplicados ignorados</summary>
                            <ul className="mt-1 pl-4 list-disc">
                              {resultadoImport.nomes_duplicados.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Exams list */}
              {loadingExames ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : exames.length === 0 ? (
                <div className="text-center py-8">
                  <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    Nenhum exame {categoriaExameAtiva === 'imagem' ? 'de imagem' : 'clínico'} cadastrado
                  </p>
                  <p className="text-sm text-gray-400 mt-1">Adicione exames usando o campo acima</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                  {exames.map((exame, index) => {
                    const editando = editandoExameId === exame.id;
                    const chipClasses = exame.categoria === 'imagem'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800';
                    const chipLabel = exame.categoria === 'imagem' ? 'Imagem' : 'Clínico';
                    return (
                      <div
                        key={exame.id}
                        className={`flex items-center justify-between px-4 py-3 transition-colors ${
                          editando ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                        data-testid={`exame-item-${exame.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-sm text-gray-400 w-6 text-right shrink-0">{index + 1}.</span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0 ${chipClasses}`}>
                            {chipLabel}
                          </span>
                          {editando ? (
                            <Input
                              value={nomeEditando}
                              onChange={(e) => setNomeEditando(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSalvarEdicaoExame();
                                if (e.key === 'Escape') handleCancelarEdicaoExame();
                              }}
                              className="flex-1 bg-white min-w-0"
                              autoFocus
                              data-testid={`input-editar-nome-${exame.id}`}
                            />
                          ) : (
                            <span className="text-gray-900 whitespace-normal break-words min-w-0 leading-relaxed">{exame.nome}</span>
                          )}
                        </div>
                        <div className="flex gap-2 ml-3 shrink-0">
                          {editando ? (
                            <>
                              <button
                                onClick={handleSalvarEdicaoExame}
                                disabled={salvandoEdicao || !nomeEditando.trim()}
                                className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
                                data-testid={`btn-salvar-edicao-${exame.id}`}
                              >
                                {salvandoEdicao ? 'Salvando...' : 'Salvar'}
                              </button>
                              <button
                                onClick={handleCancelarEdicaoExame}
                                className="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-700 hover:bg-gray-100"
                                data-testid={`btn-cancelar-edicao-${exame.id}`}
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleIniciarEdicao(exame)}
                                className="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-700 hover:bg-gray-100"
                                data-testid={`btn-editar-exame-${exame.id}`}
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleRemoverExame(exame.id)}
                                className="text-xs px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50"
                                data-testid={`btn-remover-exame-${exame.id}`}
                              >
                                Excluir
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 text-sm text-gray-500">
                {(() => {
                  const total = categoriaExameAtiva === 'imagem' ? totalExamesImagem : totalExamesClinico;
                  if (categoriaExameAtiva === 'imagem') {
                    return `${total} ${total === 1 ? 'exame de imagem cadastrado' : 'exames de imagem cadastrados'}`;
                  }
                  return `${total} ${total === 1 ? 'exame clínico cadastrado' : 'exames clínicos cadastrados'}`;
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Dados do Médico Section */}
        {activeSection === 'medico' && (
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Dados do Médico</h2>
            <p className="text-sm text-gray-500 mb-6">
              Estas informações serão utilizadas na guia de solicitação de exames.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                <Input
                  value={configMedico.nome_medico}
                  onChange={(e) => setConfigMedico({...configMedico, nome_medico: e.target.value})}
                  placeholder="Dra. Maria da Silva"
                  className="placeholder:text-gray-300"
                  data-testid="input-nome-medico"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CRM</label>
                <Input
                  value={configMedico.crm}
                  onChange={(e) => setConfigMedico({...configMedico, crm: e.target.value})}
                  placeholder="CRM/SP - 123456"
                  className="placeholder:text-gray-300"
                  data-testid="input-crm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Especialidade</label>
                <Input
                  value={configMedico.especialidade}
                  onChange={(e) => setConfigMedico({...configMedico, especialidade: e.target.value})}
                  placeholder="Ginecologia"
                  className="placeholder:text-gray-300"
                  data-testid="input-especialidade"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <Input
                  value={configMedico.telefone}
                  onChange={(e) => setConfigMedico({...configMedico, telefone: e.target.value})}
                  placeholder="(11) 9.1122-3344"
                  className="placeholder:text-gray-300"
                  data-testid="input-telefone-medico"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                <Input
                  value={configMedico.instagram}
                  onChange={(e) => setConfigMedico({...configMedico, instagram: e.target.value})}
                  placeholder="@dra.maria"
                  className="placeholder:text-gray-300"
                  data-testid="input-instagram"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                <Input
                  value={configMedico.endereco}
                  onChange={(e) => setConfigMedico({...configMedico, endereco: e.target.value})}
                  placeholder="Avenida Paulista, 1000 - 20º Andar - Sala 01 - Centro - São Paulo/SP"
                  className="placeholder:text-gray-300"
                  data-testid="input-endereco"
                />
              </div>
            </div>

            {/* Toggle QR Code */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Exibir QR Code WhatsApp nos documentos</p>
                  <p className="text-xs text-gray-500 mt-0.5">O QR Code será exibido no rodapé de guias de exames, receituários, orientações e atestados</p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfigMedico(prev => ({ ...prev, exibir_qr: !prev.exibir_qr }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    configMedico.exibir_qr ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  data-testid="toggle-exibir-qr"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      configMedico.exibir_qr ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="mt-6">
              <Button
                onClick={handleSalvarConfig}
                disabled={salvandoConfig}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="btn-salvar-config-medico"
              >
                <Save className="w-4 h-4 mr-2" />
                {salvandoConfig ? 'Salvando...' : 'Salvar Dados do Médico'}
              </Button>
            </div>
          </div>
        )}

        {/* Receituário e Orientações Section */}
        {activeSection === 'receituario' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                {editandoModelo ? 'Editar Modelo' : 'Novo Modelo'}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Cadastre modelos de receituários, orientações, atestados e modelos livres para usar no prontuário do paciente.
              </p>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Modelo</label>
                    <Input
                      value={formModelo.nome}
                      onChange={(e) => setFormModelo({...formModelo, nome: e.target.value})}
                      placeholder="Ex: Receita anticoncepcional, Atestado 1 dia..."
                      data-testid="input-nome-modelo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                    <select
                      value={formModelo.tipo}
                      onChange={(e) => setFormModelo({...formModelo, tipo: e.target.value})}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="select-tipo-modelo"
                    >
                      <option value="receituario">Receituário</option>
                      <option value="orientacao">Orientação</option>
                      <option value="atestado">Atestado</option>
                      <option value="livre">Modelo Livre</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conteúdo</label>
                  <RichTextEditor
                    value={formModelo.conteudo}
                    onChange={(html) => setFormModelo({...formModelo, conteudo: html})}
                    placeholder="Digite o conteúdo do modelo aqui..."
                    minHeight={220}
                    testId="textarea-conteudo-modelo"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSalvarModelo}
                    disabled={salvandoModelo || !formModelo.nome.trim() || !stripHtml(formModelo.conteudo)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-salvar-modelo"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {salvandoModelo ? 'Salvando...' : editandoModelo ? 'Atualizar Modelo' : 'Salvar Modelo'}
                  </Button>
                  {editandoModelo && (
                    <Button
                      onClick={handleCancelarEdicao}
                      variant="outline"
                      data-testid="btn-cancelar-edicao"
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Modelos cadastrados */}
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Modelos Cadastrados</h2>

              {loadingModelos ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : modelos.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Nenhum modelo cadastrado</p>
                  <p className="text-sm text-gray-400 mt-1">Use o formulário acima para criar modelos</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {modelos.map((modelo) => (
                    <div
                      key={modelo.id}
                      className="flex items-center justify-between py-3 px-2 hover:bg-gray-50 rounded transition-colors"
                      data-testid={`modelo-item-${modelo.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{modelo.nome}</span>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            modelo.tipo === 'receituario' ? 'bg-blue-100 text-blue-700' :
                            modelo.tipo === 'orientacao' ? 'bg-green-100 text-green-700' :
                            modelo.tipo === 'livre' ? 'bg-slate-100 text-slate-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {modelo.tipo === 'receituario' ? 'Receituário' :
                             modelo.tipo === 'orientacao' ? 'Orientação' :
                             modelo.tipo === 'livre' ? 'Modelo Livre' : 'Atestado'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2 mt-0.5 break-words">{stripHtml(modelo.conteudo).substring(0, 120)}...</p>
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        <button
                          onClick={() => handleEditarModelo(modelo)}
                          className="text-blue-500 hover:text-blue-700 p-1.5 rounded hover:bg-blue-50 transition-colors"
                          data-testid={`btn-editar-modelo-${modelo.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemoverModelo(modelo.id)}
                          className="text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50 transition-colors"
                          data-testid={`btn-remover-modelo-${modelo.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 text-sm text-gray-500">
                {modelos.length} modelo{modelos.length !== 1 ? 's' : ''} cadastrado{modelos.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp / Evolution API Section - Master Only */}
        {activeSection === 'whatsapp' && isMasterAccess && (
          <div className="space-y-6">
            {/* Evolution API Config */}
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Evolution API</h2>
              <p className="text-sm text-gray-500 mb-4">
                Configure a conexão com a Evolution API para envio de mensagens WhatsApp automáticas.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL da API</label>
                  <Input
                    value={evolutionConfig.evolution_api_url}
                    onChange={(e) => setEvolutionConfig(p => ({...p, evolution_api_url: e.target.value}))}
                    placeholder="https://sua-instancia.evolution-api.com"
                    data-testid="input-evolution-url"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                  <Input
                    type="password"
                    value={evolutionConfig.evolution_api_key}
                    onChange={(e) => setEvolutionConfig(p => ({...p, evolution_api_key: e.target.value}))}
                    placeholder="Sua chave da API"
                    data-testid="input-evolution-key"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Instância</label>
                  <Input
                    value={evolutionConfig.evolution_instance_name}
                    onChange={(e) => setEvolutionConfig(p => ({...p, evolution_instance_name: e.target.value}))}
                    placeholder="minha-instancia"
                    data-testid="input-evolution-instance"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleSalvarEvolution}
                    disabled={salvandoEvolution}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="btn-salvar-evolution"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {salvandoEvolution ? 'Salvando...' : 'Salvar Configuração'}
                  </Button>
                  <Button
                    onClick={handleTestarConexao}
                    disabled={testingConnection}
                    variant="outline"
                    className="border-green-300 text-green-700 hover:bg-green-50"
                    data-testid="btn-testar-conexao"
                  >
                    {testingConnection ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2" />
                    ) : (
                      <Wifi className="w-4 h-4 mr-2" />
                    )}
                    {testingConnection ? 'Testando...' : 'Testar Conexão'}
                  </Button>
                </div>

                {connectionStatus && (
                  <div className={`p-3 rounded-lg border ${
                    connectionStatus.success 
                      ? 'bg-green-50 border-green-200 text-green-700' 
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`} data-testid="connection-status">
                    <div className="flex items-center gap-2">
                      {connectionStatus.success ? (
                        <Wifi className="w-4 h-4" />
                      ) : (
                        <WifiOff className="w-4 h-4" />
                      )}
                      <span className="text-sm font-medium">{connectionStatus.message}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Horários de Funcionamento */}
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Horários de Funcionamento</h2>
              <p className="text-sm text-gray-500 mb-4">
                Defina os dias e horários de atendimento. Esses dados serão usados para sugerir horários alternativos ao paciente quando uma consulta for cancelada.
              </p>

              <div className="space-y-3">
                {DIAS_SEMANA.map(dia => {
                  const ativo = !!horariosFunc[dia.key];
                  return (
                    <div key={dia.key} className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                      ativo ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`} data-testid={`dia-${dia.key}`}>
                      <button
                        type="button"
                        onClick={() => toggleDia(dia.key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                          ativo ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                        data-testid={`toggle-dia-${dia.key}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          ativo ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                      <span className={`w-32 text-sm font-medium ${ativo ? 'text-gray-900' : 'text-gray-400'}`}>
                        {dia.label}
                      </span>
                      {ativo && (
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={horariosFunc[dia.key]?.inicio || '08:00'}
                            onChange={(e) => updateHorarioDia(dia.key, 'inicio', e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            data-testid={`input-inicio-${dia.key}`}
                          />
                          <span className="text-gray-500 text-sm">às</span>
                          <input
                            type="time"
                            value={horariosFunc[dia.key]?.fim || '18:00'}
                            onChange={(e) => updateHorarioDia(dia.key, 'fim', e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            data-testid={`input-fim-${dia.key}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 pt-5 border-t border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-gray-900">Duração Padrão dos Procedimentos</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Usada para calcular as sugestões de horários livres no WhatsApp de cancelamento. Vale para todos os procedimentos.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="number"
                        min="5"
                        max="480"
                        step="5"
                        value={duracaoPadraoMin}
                        onChange={(e) => setDuracaoPadraoMin(e.target.value)}
                        placeholder="Ex: 60"
                        className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 tabular-nums"
                        data-testid="input-duracao-padrao"
                      />
                      <span className="text-sm text-gray-600">minutos</span>
                      {duracaoPadraoMin && parseInt(duracaoPadraoMin, 10) >= 5 && (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-semibold border border-green-200">
                          <CheckCircle2 className="w-3 h-3" /> Configurado
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                    <Coffee className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-gray-900">Horário de Almoço</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Intervalo aplicado a todos os dias de funcionamento. As sugestões de horário no WhatsApp não conflitam com este período. Deixe vazio se não houver pausa.
                    </p>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <input
                        type="time"
                        value={almocoInicio}
                        onChange={(e) => setAlmocoInicio(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 tabular-nums"
                        data-testid="input-almoco-inicio"
                      />
                      <span className="text-sm text-gray-600">até</span>
                      <input
                        type="time"
                        value={almocoFim}
                        onChange={(e) => setAlmocoFim(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 tabular-nums"
                        data-testid="input-almoco-fim"
                      />
                      {almocoInicio && almocoFim && (
                        <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold border border-amber-200">
                          <Coffee className="w-3 h-3" /> Configurado
                        </span>
                      )}
                      {(almocoInicio || almocoFim) && (
                        <button
                          type="button"
                          onClick={() => { setAlmocoInicio(''); setAlmocoFim(''); }}
                          className="ml-2 text-[11px] font-medium text-gray-500 hover:text-red-600 transition-colors"
                          data-testid="btn-limpar-almoco"
                        >
                          Limpar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Button
                  onClick={handleSalvarHorarios}
                  disabled={salvandoHorarios}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="btn-salvar-horarios"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {salvandoHorarios ? 'Salvando...' : 'Salvar Horários e Duração'}
                </Button>
              </div>
            </div>

            {/* Template de Mensagem */}
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Template de Cancelamento</h2>
              <p className="text-sm text-gray-500 mb-2">
                Mensagem enviada ao paciente quando o médico cancela uma consulta. Use as variáveis abaixo:
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {['{nome_paciente}', '{procedimento}', '{data}', '{horario}', '{horarios_sugeridos}'].map(v => (
                  <span key={v} className="inline-flex items-center px-2.5 py-1 rounded-md bg-green-100 text-green-800 text-xs font-mono">
                    {v}
                  </span>
                ))}
              </div>

              {(() => {
                const algumDiaAtivo = Object.values(horariosFunc || {}).some(v => v && (v.inicio || v.fim));
                const duracaoOk = duracaoPadraoMin !== '' && parseInt(duracaoPadraoMin, 10) >= 5;
                const prerequisitosOk = algumDiaAtivo && duracaoOk;
                if (prerequisitosOk) return null;
                return (
                  <div className="mb-4 rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="template-prerequisitos-warning">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                    <div className="flex-1 text-sm text-amber-900">
                      <p className="font-bold mb-1">Antes de salvar o template, configure:</p>
                      <ul className="space-y-0.5 text-[13px]">
                        <li className={algumDiaAtivo ? 'line-through opacity-60' : ''}>
                          {algumDiaAtivo ? '✓' : '•'} Ative ao menos um dia da semana em <strong>Horários de Funcionamento</strong>
                        </li>
                        <li className={duracaoOk ? 'line-through opacity-60' : ''}>
                          {duracaoOk ? '✓' : '•'} Defina a <strong>Duração Padrão</strong> dos procedimentos (mínimo 5 minutos)
                        </li>
                      </ul>
                    </div>
                  </div>
                );
              })()}

              <textarea
                value={templateCancel}
                onChange={(e) => setTemplateCancel(e.target.value)}
                rows={8}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-y"
                placeholder="Olá {nome_paciente}, informamos que sua consulta..."
                data-testid="textarea-template-cancel"
              />

              <div className="mt-4">
                {(() => {
                  const algumDiaAtivo = Object.values(horariosFunc || {}).some(v => v && (v.inicio || v.fim));
                  const duracaoOk = duracaoPadraoMin !== '' && parseInt(duracaoPadraoMin, 10) >= 5;
                  const prerequisitosOk = algumDiaAtivo && duracaoOk;
                  return (
                    <Button
                      onClick={handleSalvarTemplate}
                      disabled={salvandoTemplate || !templateCancel.trim() || !prerequisitosOk}
                      className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="btn-salvar-template"
                      title={!prerequisitosOk ? 'Configure Horários de Funcionamento e Duração Padrão primeiro' : ''}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {salvandoTemplate ? 'Salvando...' : 'Salvar Template'}
                    </Button>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Financeiro Section (Master only) */}
        {activeSection === 'financeiro' && isMasterAccess && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Recurso Financeiro</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Controle a exibição de pendências financeiras no Painel de Pendências de Consultas.
                    Independente do toggle, o módulo financeiro da consulta continua disponível para registrar pagamentos manualmente.
                  </p>
                </div>
              </div>

              {/* Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50/50 mb-4">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm font-semibold text-gray-900">Habilitar pendências financeiras</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Quando ativo, consultas originais sem pagamento aparecerão no banner de pendências.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFinanceiroEnabled(v => !v)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    financeiroEnabled ? 'bg-amber-500' : 'bg-gray-300'
                  }`}
                  data-testid="toggle-financeiro"
                  aria-checked={financeiroEnabled}
                  role="switch"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      financeiroEnabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Data de início (only when enabled) */}
              {financeiroEnabled && (
                <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/50 mb-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-1">
                    Data de início da cobrança de pendências
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Apenas consultas a partir desta data aparecerão no banner de pendências financeiras.
                    Se ficar em branco, todas as consultas elegíveis serão consideradas.
                  </p>
                  <Input
                    type="date"
                    value={financeiroDataInicio}
                    onChange={(e) => setFinanceiroDataInicio(e.target.value)}
                    className="max-w-xs"
                    data-testid="input-financeiro-data-inicio"
                  />
                  {financeiroDataInicio && (
                    <p className="text-xs text-amber-800 mt-2 font-medium">
                      Consultas anteriores a {(() => { const [y,m,d]=financeiroDataInicio.split('-'); return `${d}/${m}/${y}`; })()} não aparecerão no banner.
                    </p>
                  )}
                </div>
              )}

              {/* Save button */}
              <div className="mt-4">
                <Button
                  onClick={handleSalvarFinanceiro}
                  disabled={salvandoFinanceiro}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="btn-salvar-financeiro"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {salvandoFinanceiro ? 'Salvando...' : 'Salvar Configuração'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
